"use client";

import React from "react";
import { ShieldCheck, Award, Settings, MapPin, ArrowRight, Building2, Factory, HardHat } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  value: "10+",
  label: "Anni di Esperienza"
}, {
  value: "100%",
  label: "Conformità Normativa"
}, {
  value: "360°",
  label: "Supporto Integrato"
}];
const benefits = [{
  title: "Competenza Tecnica",
  desc: "Aggiornamento continuo sulle normative D.Lgs 81/08 e soluzioni ingegneristiche all'avanguardia.",
  icon: ShieldCheck
}, {
  title: "Affidabilità Riconosciuta",
  desc: "Un partner solido e professionale per la gestione completa della sicurezza aziendale.",
  icon: Award
}, {
  title: "Soluzioni su Misura",
  desc: "Approccio personalizzato per rispondere alle esigenze specifiche di ogni settore operativo.",
  icon: Settings
}, {
  title: "Presenza Capillare",
  desc: "Operativi in tutta la Lombardia, con un focus strategico su Monza, Milano e Brianza.",
  icon: MapPin
}];
const sectors = [{
  id: "edilizia",
  title: "Imprese Edili",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585744/img/settore_edilizia_cantieri.png",
  desc: "Sicurezza cantieri CSP/CSE, progettazione strutturale e direzione lavori per costruzioni sicure.",
  icon: HardHat
}, {
  id: "impianti",
  title: "Aziende Impiantistiche",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585744/img/settore_impianti_industriali.png",
  desc: "Conformità normativa, progettazione antincendio e gestione dei rischi specifici del settore.",
  icon: Factory
}, {
  id: "pmi",
  title: "PMI e Manifatturiero",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1585744/img/settore_pmi_manifattura.png",
  desc: "Incarichi RSPP esterno, valutazione dei rischi (DVR) e formazione continua per i lavoratori.",
  icon: Building2
}];
export default function EccellenzaESettoriOperativi() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="eccellenza-e-settori-operativi" className="bg-background py-24 lg:py-32">
      <div ref={ref} className={`container mx-auto px-4 transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Eccellenza e Settori Operativi
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Soluzioni ingegneristiche integrate per garantire la massima sicurezza e supportare lo sviluppo del tuo business in ogni settore.
          </p>
        </div>

        <Tabs defaultValue="perche-sceglierci" className="mx-auto max-w-5xl">
          <div className="mb-12 flex justify-center">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="perche-sceglierci" className="text-base">
                Perché Sceglierci
              </TabsTrigger>
              <TabsTrigger value="settori" className="text-base">
                Settori di Attività
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="perche-sceglierci" className="mt-0 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="grid gap-12 lg:grid-cols-12">
              {}
              <div className="flex flex-col justify-center gap-6 lg:col-span-4">
                {stats.map((stat, i) => <div key={i} data-index={i} className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center shadow-sm transition-shadow hover:shadow-md">
                    <div className="absolute -right-4 -top-4 size-24 rounded-full bg-primary/5 blur-2xl" />
                    <span className="font-mono text-5xl font-bold tracking-tighter text-primary">
                      {stat.value}
                    </span>
                    <span className="mt-3 block text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      {stat.label}
                    </span>
                  </div>)}
              </div>

              {}
              <div className="lg:col-span-8">
                <div className="grid gap-6 sm:grid-cols-2">
                  {benefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return <Card key={i} data-index={i} className="border-border/50 bg-card/50 backdrop-blur-sm transition-colors hover:bg-card">
                        <CardHeader>
                          <div className="mb-4 inline-flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Icon className="size-6" />
                          </div>
                          <CardTitle className="text-xl">{benefit.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <CardDescription className="text-base text-muted-foreground">
                            {benefit.desc}
                          </CardDescription>
                        </CardContent>
                      </Card>;
                })}
                </div>
                <div className="mt-8 flex justify-end">
                  <Button variant="outline" className="group">
                    Scopri il nostro approccio
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settori" className="mt-0 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="grid gap-6 md:grid-cols-3">
              {sectors.map((sector, i) => {
              const Icon = sector.icon;
              return <div key={i} data-index={i} className="group relative flex h-[400px] flex-col justify-end overflow-hidden rounded-xl bg-muted">
                    {}
                    <img src={sector.image} alt={sector.title} className="absolute inset-0 h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105" />
                    {}
                    <div className="absolute inset-0 bg-primary/85 transition-opacity duration-500 group-hover:bg-primary/75" />
                    
                    {}
                    <div className="relative z-10 p-8">
                      <div className="mb-4 inline-flex size-12 items-center justify-center rounded-lg bg-white/10 text-white backdrop-blur-md">
                        <Icon className="size-6" />
                      </div>
                      <h3 className="mb-3 text-2xl font-bold text-primary-foreground">
                        {sector.title}
                      </h3>
                      <p className="text-primary-foreground/80 leading-relaxed">
                        {sector.desc}
                      </p>
                    </div>
                    
                    {}
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px] opacity-20" />
                  </div>;
            })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}